import React, { useState, useRef, useEffect, ReactNode } from 'react'

import classNames from 'classnames'
import { isEmpty } from 'lodash'

import { Icon } from '@/Atomic/Atoms/Icon'
import { Avatar } from '@/Atomic/Molecules/Avatar'

import './ProfileMenu.scss'

export interface ProfileMenuItemProps {
  id: string
  icon?: ReactNode
  label: string
  isActive?: boolean
  variant?: 'default' | 'danger'
  onClick: () => void
}

interface UserAccount {
  email?: string
  name?: string
  picture?: string
  given_name?: string
  family_name?: string
}

export interface ProfileMenuProps extends React.HTMLAttributes<HTMLDivElement> {
  user: UserAccount
  menuItems: ProfileMenuItemProps[]
  className?: string
}

function getInitialsFromUser(user: UserAccount): string | undefined {
  const givenName = user.given_name?.trim()
  const familyName = user.family_name?.trim()
  if (givenName || familyName) {
    const first = givenName?.charAt(0) || ''
    const last = familyName?.charAt(0) || ''
    const combined = `${first}${last}`.trim()
    return combined ? combined.toUpperCase() : undefined
  }

  const name = user.name?.trim()
  if (!name) return undefined
  const tokens = name.split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return undefined
  if (tokens.length === 1) return tokens[0]?.slice(0, 2).toUpperCase()

  const first = tokens[0]
  const last = tokens[tokens.length - 1]
  if (!first || !last) return undefined
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase()
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  user,
  menuItems,
  className,
  ...divAttributes
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as HTMLElement)
      ) {
        if (isOpen) setIsOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside, false)
    return () => {
      document.removeEventListener('click', handleClickOutside, false)
    }
  }, [isOpen])

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  const handleItemClick = (item: ProfileMenuItemProps) => {
    item.onClick()
    setIsOpen(false)
  }

  const initials = getInitialsFromUser(user)
  const hasUserPicture = !isEmpty(user.picture)
  const showInitials = !hasUserPicture && !isEmpty(initials)
  const showIconOnly = !hasUserPicture && isEmpty(initials)

  return (
    <div
      ref={containerRef}
      className={classNames('profile-menu-container', className, {
        open: isOpen,
      })}
      {...divAttributes}
    >
      {/* Trigger - Avatar button */}
      <div
        className={classNames('profile-menu-trigger', { active: isOpen })}
        onClick={handleTriggerClick}
      >
        <Avatar
          avatar={hasUserPicture ? user.picture : undefined}
          initials={showInitials ? initials : undefined}
          icon={showIconOnly ? 'user' : undefined}
          className="profile-avatar"
        />
        <div className="profile-info">
          <span className="profile-name">{user.name}</span>
          <span className="profile-email">{user.email}</span>
        </div>
      </div>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="profile-dropdown-panel">
          <div className="profile-dropdown-list">
            {menuItems.map((item) => (
              <div
                key={item.id}
                className={classNames('profile-dropdown-item', {
                  active: item.isActive,
                  'profile-danger-item': item.variant === 'danger',
                })}
                onClick={() => handleItemClick(item)}
                role="menuitem"
              >
                {item.icon && (
                  <span
                    className={classNames('profile-item-icon', {
                      'profile-danger-icon': item.variant === 'danger',
                    })}
                  >
                    {item.icon}
                  </span>
                )}
                <span className="profile-dropdown-label">{item.label}</span>
                {item.isActive && (
                  <Icon icon="check" className="profile-check-icon" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfileMenu
